/**
 * LanguageSwitcher - dropdown drapeau + code 2 lettres.
 * Usage dans TopBar + LoginPage. Sauvegarde dans localStorage.
 */
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { tokens } from '@fluentui/react-components';
import { SUPPORTED_LANGUAGES } from '../i18n';

export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = SUPPORTED_LANGUAGES.find(l => l.code === i18n.language)
    ?? SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-label="Choose language"
        style={{
          background: 'transparent',
          border: `1px solid ${tokens.colorNeutralStroke2}`,
          padding: '6px 12px',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 600,
          color: tokens.colorNeutralForeground1,
          fontFamily: 'inherit',
        }}
      >
        <span style={{ fontSize: 16 }}>{current.flag}</span>
        <span>{current.code.split('-')[0].toUpperCase()}</span>
        <span style={{ fontSize: 9, opacity: 0.6 }}>▼</span>
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            background: tokens.colorNeutralBackground1,
            border: `1px solid ${tokens.colorNeutralStroke2}`,
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(15,38,56,.15)',
            minWidth: 160,
            padding: 4,
            zIndex: 1000,
          }}
        >
          {SUPPORTED_LANGUAGES.map(lang => {
            const active = lang.code === current.code;
            return (
              <button
                key={lang.code}
                onClick={() => {
                  // Persiste explicitement dans localStorage. La cle DOIT
                  // matcher lookupLocalStorage dans src/i18n/index.ts.
                  try {
                    window.localStorage.setItem('nis365-portal-lang', lang.code);
                  } catch { /* ignore mode prive / quota */ }
                  i18n.changeLanguage(lang.code);
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '8px 12px',
                  border: 0,
                  background: active ? tokens.colorBrandBackground2 : 'transparent',
                  color: active ? tokens.colorBrandForeground1 : tokens.colorNeutralForeground1,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  borderRadius: 6,
                  textAlign: 'left',
                  fontFamily: 'inherit',
                }}
              >
                <span style={{ fontSize: 18 }}>{lang.flag}</span>
                <span>{lang.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
